/**
 * The preview column: the zoom bar, the `<svg>` stage, every draggable handle, and the legend.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-11 / AUD-2 RC-4) as a PURE presentational component —
 * it imports neither `useEditorZoom` nor `useMaskGeometry`, it takes their output as props. That
 * is what makes the canvas renderable (and assertable) without mounting the editor, which is the
 * precondition for J-18's keyboard-access work.
 *
 * **Every `data-testid` is unchanged** (`editor-preview`, `zoom-controls`, `print-vertex-{i}`,
 * `brand-area-quad`, `mask-editor__legend` …): 73 + 32 unedited component tests select on them,
 * and they are the guard that this move changed nothing.
 *
 * J-11 is a pure move. Nothing about accessibility changes here — that is J-18.
 */
import { useGuideSafeUrl } from "../generate/whiteGuide.js";
import { useMemo, type ReactNode } from "react";
import type { Mask, Point, Polygon } from "@genz/engine-bind";
import type { Target } from "./useMaskGeometry.js";
import { clampZoom, MAX_ZOOM } from "./zoom.js";
import { clamp01 } from "./pixels.js";
import { Icon } from "../ui/Icon.js";

export interface MaskCanvasProps {
  /** Resolved display size (J-6) — the SVG's coordinate system. */
  w: number;
  h: number;
  /** Zoom/pan (J-4), passed in rather than imported so the canvas stays presentational. */
  zoom: number;
  setZoom: (z: number) => void;
  center: Point;
  setCenter: (c: Point) => void;
  vb: { x: number; y: number; w: number; h: number };
  hr: number;
  hitr: number;
  sw: number;
  zoomBy: (f: number, focal?: Point) => void;
  resetZoom: () => void;
  svgRef: React.RefObject<SVGSVGElement | null>;

  /** What to show under the handles: the live composite, else the original case image. */
  displayUrl?: string;
  caseImageUrl?: string;
  /** The clear case, drawn under that image: a keyed (transparent) print area shows it. */
  underlayUrl?: string;

  /** Geometry (J-10), also passed in. */
  printArea: Polygon;
  cutouts: Mask[];
  effective: Polygon;
  brandArea?: Polygon | null;
  onBrandAreaChange?: (p: Polygon) => void;
  printAreaActive: boolean;
  setPrintAreaActive: (v: boolean) => void;
  activeCutout: number;
  setActiveCutout: (i: number) => void;
  selectedPrintVertex: number | null;
  setSelectedPrintVertex: (i: number | null) => void;
  drag: Target | null;
  setDrag: (t: Target | null) => void;
  toNorm: (clientX: number, clientY: number) => Point;
  onPointerMove: (e: React.PointerEvent) => void;
  endDrag: () => void;
  onBackgroundClick: (e: React.MouseEvent) => void;
  deleteVertex: (t: Target) => void;

  locked?: boolean;
  /** Eyedropper armed → the stage shows the crosshair cursor instead of the grab cursor. */
  markerEyedrop: boolean;
  /** Both only affect what the legend and the backdrop show. */
  removed: boolean;
  bgMode: string;
  /**
   * A page-level status line for THIS view, rendered at the top of the preview column
   * (WS-ERR F-2). It belongs here and not in a rail card because the rail is an exclusive
   * accordion: the operator presses "Preview mock-up" from the "Design fit & aspect" section,
   * so a message rendered in the "Remove background (AI)" body lands in a drawer that is shut
   * at exactly that moment. The preview column is never collapsed.
   */
  statusSlot?: ReactNode;
  /** WS-AUTOKEY fix round — `"placement"` is a customer's canvas: the four print-area corners
   *  MOVE (design placement) and nothing else. No point is added (shift/alt-click) or removed
   *  (double-click, Delete/Backspace), and cutouts are drawn without handles and cannot be
   *  grabbed. Any other value, or none, is the full canvas. */
  railMode?: "placement" | "basic" | "advanced";
}

export function MaskCanvas(p: MaskCanvasProps) {
  const {
    w, h, zoom, setZoom, center,
    setCenter, vb, hr, hitr, sw,
    zoomBy, resetZoom, svgRef, displayUrl, caseImageUrl, underlayUrl,
    printArea, cutouts, effective, brandArea, onBrandAreaChange,
    printAreaActive, setPrintAreaActive, activeCutout, setActiveCutout, selectedPrintVertex,
    setSelectedPrintVertex, drag, setDrag, toNorm, onPointerMove,
    endDrag, onBackgroundClick, deleteVertex, locked, markerEyedrop,
    removed, bgMode, statusSlot,
  } = p;
  const safeCaseUrl = useGuideSafeUrl(caseImageUrl);
  const placementOnly = p.railMode === "placement";
  const points = (poly: Polygon) => poly.map((p) => `${p.x * w},${p.y * h}`).join(" ");
  // MN-11 / review-1 M-3: `points` is re-created every render, so listing it would defeat the
  // memo. It is a pure function of `w`/`h`, which ARE listed — so inline the one line the memo
  // needs and the dep list becomes complete without changing what it recomputes on.
  // WS-DET F-7 — the legend calls this "Effective (print − cutouts)", so it has to be that.
  // The engine's `effective_print_area` returns the OUTER ring only (a single-ring `Polygon`
  // cannot carry holes), so the dashed outline used to be a duplicate of the print-area ring
  // and the subtraction it promised never appeared on the canvas. The holes are drawn here,
  // where the cutouts already are: one subpath for the outer ring, one per cutout, `evenodd`
  // so the enclosed region is print − cutouts. Still unfilled — a wash over the artwork is
  // what the outline-only rule (above) exists to prevent.
  const effectivePath = useMemo(() => {
    const ring = (poly: Polygon) =>
      poly.length < 3 ? "" : `M${poly.map((p) => `${p.x * w},${p.y * h}`).join("L")}Z`;
    return [ring(effective), ...cutouts.map((c) => ring(c.polygon))].filter(Boolean).join(" ");
  }, [effective, cutouts, w, h]);
  return (
      <div
        className="mask-editor__preview"
        data-testid="editor-preview"
        style={{
          gridColumn: 1,
          gridRow: 1,
          minWidth: 0,
          // Sticky preview: pinned to the top of its column so it stays in view while
          // the (taller) right-hand control panel scrolls past.
          position: "sticky",
          top: "var(--space-4, 1rem)",
          alignSelf: "start",
        }}
      >
      {/* WS-ERR F-2 — the page-level status line for this view, ABOVE the canvas and outside
          the rail's accordion. `MaskEditor` fills it; it renders nothing when there is
          nothing to say. */}
      {statusSlot}
      {/* #3 — Zoom controls: scroll-to-zoom on the canvas, or use these. Pan by dragging
          the canvas when zoomed. Lets the operator place the 4 corners precisely. */}
      <div className="mask-editor__zoom" data-testid="zoom-controls">
        <span className="label">Zoom</span>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => zoomBy(1 / 1.25)}
          disabled={zoom <= 1}
          aria-label="Zoom out"
          data-testid="zoom-out"
        >
          <Icon name="minus" size={16} />
        </button>
        <input
          type="range"
          min={1}
          max={MAX_ZOOM}
          step={0.1}
          value={zoom}
          onChange={(e) => {
            const nz = clampZoom(Number(e.target.value));
            setZoom(nz);
            if (nz <= 1) setCenter({ x: 0.5, y: 0.5 });
          }}
          aria-label="Zoom level"
          data-testid="zoom-range"
        />
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => zoomBy(1.25, center)}
          disabled={zoom >= MAX_ZOOM}
          aria-label="Zoom in"
          data-testid="zoom-in"
        >
          <Icon name="plus" size={16} />
        </button>
        <span className="mask-editor__zoom-level" data-testid="zoom-level">{zoom.toFixed(1)}×</span>
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          onClick={resetZoom}
          disabled={zoom === 1}
          data-testid="zoom-reset"
        >
          Reset
        </button>
      </div>
      <div className="mask-editor__canvas">
        {/* Checkerboard behind the preview when the background is removed AND set to transparent,
            so the transparency reads as transparency (not opaque white). */}
        <div
          className={`mask-editor__stage${removed && bgMode === "transparent" ? " mask-editor__canvas--checker" : ""}`}
          style={{ position: "relative" }}
        >
          <svg
            ref={svgRef}
            width={w}
            height={h}
            viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
            style={{
              touchAction: "none",
              maxWidth: "100%",
              height: "auto",
              display: "block",
              cursor: markerEyedrop ? "crosshair" : drag?.kind === "pan" ? "grabbing" : zoom > 1 ? "grab" : undefined,
            }}
            onPointerDown={(e) => {
              // A press that reaches the SVG itself missed every shape, so it is a click
              // "outside" — drop the print-area fill and any selected corner.
              setPrintAreaActive(false);
              setSelectedPrintVertex(null);
              // Empty-canvas drag pans the view when zoomed in (handles stop propagation,
              // and add-vertex needs alt/shift, so a plain drag here is unambiguous).
              if (zoom <= 1 || e.altKey || e.shiftKey) return;
              setDrag({ kind: "pan", startClient: { x: e.clientX, y: e.clientY }, startCenter: center });
            }}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            onClick={placementOnly ? undefined : onBackgroundClick}
            // F-39 / AUD-2 IM-8: "the whole canvas is an unlabelled graphic". A `<title>` as the
            // first child is what SVG itself uses for an accessible name; `role="img"` +
            // `aria-label` is what assistive tech reads from the host document.
            role="img"
            aria-label="Mask editor canvas — print area, cutouts and brand logo box"
          >
            <title>Mask editor canvas — print area, cutouts and brand logo box</title>
            {underlayUrl && <image href={underlayUrl} x={0} y={0} width={w} height={h} />}
            {(displayUrl ?? safeCaseUrl) && (
              <image href={displayUrl ?? safeCaseUrl} x={0} y={0} width={w} height={h} />
            )}

            {/* Effective print area (print minus cutouts) — preview before lock.
                Colors come from the --mask-effective token (same value as before). */}
            {/* Outline only — the old 18% wash sat over the artwork, so its colours could
                never be judged in place ("the colour is not coming correct"). */}
            <path
              data-testid="effective-outline"
              d={effectivePath}
              fillRule="evenodd"
              strokeDasharray="6 4"
              style={{ fill: "none", stroke: "var(--mask-effective)", pointerEvents: "none" }}
            />

            {/* Raw print-area polygon — uses the --mask-print token. NEVER filled: even the
                selected-state 12% wash shifted the artwork's colours exactly while the operator
                was judging them. Selection shows on the outline (width) instead; `transparent`
                (not `none`) keeps the interior clickable to select. */}
            <polygon
              points={points(printArea)}
              strokeWidth={sw}
              data-testid="print-area-body"
              onPointerDown={(e) => {
                e.stopPropagation(); // don't let the svg handler immediately deselect
                setPrintAreaActive(true);
                if (locked) return;
                // Owner, 2026-10-13: drag inside the area to move it whole, design included.
                setSelectedPrintVertex(null);
                setDrag({ kind: "print-move", start: toNorm(e.clientX, e.clientY), original: printArea });
              }}
              style={{
                fill: "transparent",
                stroke: "var(--mask-print)",
                strokeWidth: printAreaActive ? sw * 1.8 : sw,
                cursor: locked ? undefined : "move",
              }}
            />
            {!locked &&
              printArea.map((p, i) => (
                <g key={`pa-${i}`}>
                  {/* Visible corner dot — purely decorative; the transparent ring below
                      carries the pointer events so the grab area is larger than the dot. */}
                  <circle
                    cx={p.x * w}
                    cy={p.y * h}
                    r={hr}
                    style={{ fill: "var(--mask-print)", pointerEvents: "none" }}
                    data-selected={selectedPrintVertex === i ? "true" : undefined}
                  />
                  {/* Enlarged invisible hit-target for easy dragging. ED-14: also FOCUSABLE, so a
                      keyboard user can Tab to a corner (focus selects it) and nudge it with the
                      arrow keys — previously print-area vertices were pointer-only and the arrow
                      keys could never reach them. Delete/Backspace removes it (the >=3 guard in
                      `deleteVertex` still applies), mirroring the double-click. */}
                  <circle
                    cx={p.x * w}
                    cy={p.y * h}
                    r={hitr}
                    style={{ fill: "transparent", cursor: "grab", outlineOffset: 2 }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Print area corner ${i + 1} of ${printArea.length}`}
                    onFocus={() => { setSelectedPrintVertex(i); setPrintAreaActive(true); }}
                    onKeyDown={(e) => {
                      if (placementOnly) return;
                      if (e.key !== "Delete" && e.key !== "Backspace") return;
                      e.preventDefault();
                      e.stopPropagation();
                      deleteVertex({ kind: "print", index: i });
                    }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      // Select this print-area vertex so the arrow keys nudge IT. Grabbing a
                      // corner is also "working on the print area", so the fill comes back —
                      // it is the guide you want visible while dragging its boundary.
                      setSelectedPrintVertex(i);
                      setPrintAreaActive(true);
                      setDrag({ kind: "print", index: i });
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (placementOnly) return;
                      deleteVertex({ kind: "print", index: i });
                    }}
                    data-testid={`print-vertex-${i}`}
                  />
                </g>
              ))}

            {/* Spec 017 US6: brand-logo placement RECTANGLE (amber). Body drags to reposition;
                the four corner handles resize it, always staying an axis-aligned rectangle. */}
            {brandArea && brandArea.length >= 3 && (
              <>
                <polygon
                  points={points(brandArea)}
                  fill="rgba(243,156,18,0.14)"
                  stroke="#f39c12"
                  strokeWidth={sw}
                  strokeDasharray="4 3"
                  data-testid="brand-area-quad"
                  onPointerDown={
                    locked || !onBrandAreaChange
                      ? undefined
                      : (e) => {
                          e.stopPropagation();
                          setDrag({ kind: "brand-move", start: toNorm(e.clientX, e.clientY), original: brandArea! });
                        }
                  }
                  // F-39: the box already had a `brand-move` DRAG kind; this is the same motion
                  // from the keyboard. All four corners translate together — it is a move, not a
                  // resize — and the step matches the arrow-key nudge elsewhere (1px, Shift = 10).
                  {...(locked || !onBrandAreaChange ? {} : {
                    tabIndex: 0,
                    role: "button",
                    "aria-label": "Brand logo box",
                    onKeyDown: (e: React.KeyboardEvent) => {
                      const step = (e.shiftKey ? 10 : 1) / 400;
                      let dx = 0, dy = 0;
                      if (e.key === "ArrowLeft") dx = -step;
                      else if (e.key === "ArrowRight") dx = step;
                      else if (e.key === "ArrowUp") dy = -step;
                      else if (e.key === "ArrowDown") dy = step;
                      else return;
                      e.preventDefault();
                      e.stopPropagation();
                      onBrandAreaChange(brandArea!.map((q) => ({ x: clamp01(q.x + dx), y: clamp01(q.y + dy) })));
                    },
                  })}
                  style={locked ? undefined : { cursor: "move", outlineOffset: 2 }}
                />
                {!locked &&
                  brandArea.map((p, i) => (
                    <circle
                      key={`ba-${i}`}
                      cx={p.x * w}
                      cy={p.y * h}
                      r={hr}
                      fill="#f39c12"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        setDrag({ kind: "brand", index: i });
                      }}
                      // F-39: the same treatment the print-area corners already have.
                      tabIndex={0}
                      role="button"
                      aria-label={`Brand logo box corner ${i + 1} of 4`}
                      data-testid={`brand-vertex-${i}`}
                      style={{ cursor: "grab", outlineOffset: 2 }}
                    />
                  ))}
              </>
            )}

            {/* Cutout masks */}
            {cutouts.map((c, ci) => (
              <g key={`cut-${ci}`}>
                <polygon
                  points={points(c.polygon)}
                  strokeWidth={ci === activeCutout ? sw : sw / 2}
                  style={{
                    fill: "color-mix(in srgb, var(--mask-cutout) 18%, transparent)",
                    stroke: "var(--mask-cutout)",
                    cursor: locked || placementOnly ? "default" : "move",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveCutout(ci);
                  }}
                  onPointerDown={(e) => {
                    // Click the cutout body/edge → drag the WHOLE shape (vertices
                    // keep their own handles for fine point edits).
                    if (locked || placementOnly) return;
                    e.stopPropagation();
                    setActiveCutout(ci);
                    setSelectedPrintVertex(null); // grabbing a cutout deselects the print vertex
                    setDrag({
                      kind: "cutout-move",
                      cutout: ci,
                      start: toNorm(e.clientX, e.clientY),
                      original: c.polygon,
                    });
                  }}
                />
                {!locked && !placementOnly &&
                  c.polygon.map((p, i) => (
                    <rect
                      key={`cut-${ci}-${i}`}
                      x={p.x * w - hr}
                      y={p.y * h - hr}
                      width={hr * 2}
                      height={hr * 2}
                      style={{ fill: "var(--mask-cutout)", cursor: "grab", outlineOffset: 2 }}
                      // F-39 / AUD-2 IM-8: the print-area corners got this treatment and "the fix
                      // stopped one shape short". Focusing a cutout vertex is what lets a keyboard
                      // user CHOOSE which cutout the (already shipping, ED-14) arrow-key nudge
                      // applies to — without it that nudge was unreachable from the keyboard.
                      tabIndex={0}
                      role="button"
                      aria-label={`Cutout ${ci + 1} corner ${i + 1} of ${c.polygon.length}`}
                      data-testid={`cutout-${ci}-vertex-${i}`}
                      onFocus={() => {
                        setActiveCutout(ci);
                        setSelectedPrintVertex(null); // …exactly as grabbing it with the pointer does
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Delete" && e.key !== "Backspace") return;
                        e.preventDefault();
                        e.stopPropagation();
                        deleteVertex({ kind: "cutout", cutout: ci, index: i });
                      }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        setActiveCutout(ci);
                        setSelectedPrintVertex(null); // grabbing a cutout point deselects the print vertex
                        setDrag({ kind: "cutout", cutout: ci, index: i });
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        deleteVertex({ kind: "cutout", cutout: ci, index: i });
                      }}
                    />
                  ))}
              </g>
            ))}
          </svg>
        </div>
      </div>

      <div className="mask-editor__legend" aria-hidden="true">
        <span className="mask-editor__legend-item">
          <span className="mask-editor__swatch mask-editor__swatch--print" /> Print area
        </span>
        <span className="mask-editor__legend-item">
          <span className="mask-editor__swatch mask-editor__swatch--cutout" /> Cutout
        </span>
        <span className="mask-editor__legend-item">
          <span className="mask-editor__swatch mask-editor__swatch--effective" /> Effective (print − cutouts)
        </span>
      </div>
      </div>
  );
}
